/**
 * BookingConfirmation.jsx
 * Success page shown after booking is created and payment confirmed.
 * Route: /booking/:id/confirmation
 *
 * Fetches live booking data to show:
 *   - Contract signing status (Tesla vehicles)
 *   - Guest key status + access link (Tesla vehicles)
 *   - Loyalty points earned
 *   - Deposit hold info
 *   - Tesla vs non-Tesla "what's next" instructions
 */
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi } from '../context/AuthContext';

// ── Status badge helpers ──────────────────────────────────────────────────────

const CONTRACT_LABELS = {
  not_required: null,
  pending:      { label: 'Pending',  color: 'bg-yellow-100 text-yellow-700' },
  sent:         { label: 'Awaiting Signature', color: 'bg-blue-100 text-blue-700' },
  viewed:       { label: 'Viewed',   color: 'bg-blue-100 text-blue-700' },
  renter_signed:{ label: 'Awaiting Owner Signature', color: 'bg-yellow-100 text-yellow-700' },
  signed:       { label: 'Signed ✓', color: 'bg-green-100 text-green-700' },
  expired:      { label: 'Expired',  color: 'bg-red-100 text-red-700' },
  declined:     { label: 'Declined', color: 'bg-red-100 text-red-700' },
};

const GUEST_KEY_LABELS = {
  not_applicable:      null,
  // Legacy statuses (kept for backward compat)
  pending:             { label: 'Scheduled',          color: 'bg-yellow-100 text-yellow-700', link: false },
  active:              { label: 'Active ✓',            color: 'bg-green-100 text-green-700',  link: false },
  revoked:             { label: 'Revoked',             color: 'bg-gray-100 text-gray-500',    link: false },
  // New Guest Mode statuses
  page_ready:          { label: 'Portal Ready →',      color: 'bg-blue-100 text-blue-700',    link: true  },
  guest_mode_active:   { label: 'Guest Mode Active ✓', color: 'bg-green-100 text-green-700',  link: false },
  guest_mode_disabled: { label: 'Access Ended',        color: 'bg-gray-100 text-gray-500',    link: false },
  failed:              { label: 'Failed',              color: 'bg-red-100 text-red-700',      link: false },
};

function StatusBadge({ label, color, href }) {
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`px-2.5 py-1 rounded-full text-xs font-semibold underline decoration-dotted hover:opacity-80 transition-opacity cursor-pointer ${color}`}
      >
        {label}
      </a>
    );
  }
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${color}`}>
      {label}
    </span>
  );
}

function formatCents(c) {
  return `$${((c || 0) / 100).toFixed(2)}`;
}

// ── Loading skeleton ──────────────────────────────────────────────────────────

function Skeleton() {
  return (
    <div className="min-h-screen bg-gray-50 py-16 px-4">
      <div className="max-w-lg mx-auto space-y-4 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-2/3 mx-auto" />
        <div className="h-4 bg-gray-200 rounded w-1/3 mx-auto" />
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-4 bg-gray-100 rounded" />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function BookingConfirmation() {
  const { id } = useParams();
  const api    = useApi();

  const [booking, setBooking] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  // The Stripe webhook confirms payment asynchronously, so right after
  // checkout the booking may still read "pending" for a few seconds.
  // Poll briefly so status/points/contract info update without a manual
  // refresh, instead of freezing on the first (possibly stale) fetch.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    let pollCount = 0;
    const MAX_POLLS = 15; // ~30s at 2s intervals

    const fetchBooking = () => {
      api.get(`/bookings/${id}`)
        .then(r => {
          if (cancelled) return;
          setBooking(r.data);
          if (r.data?.vin) {
            api.get(`/vehicles/${r.data.vin}`).then(vr => { if (!cancelled) setVehicle(vr.data); }).catch(() => {});
          }
          pollCount += 1;
          // Contract sending happens right after status flips to confirmed
          // (a separate SignWell API call), so keep polling briefly for that
          // too instead of stopping the instant status looks done.
          const stillSettling = r.data?.status === 'pending' || r.data?.contractStatus === 'pending';
          if (stillSettling && pollCount < MAX_POLLS) {
            setTimeout(fetchBooking, 2000);
          }
        })
        .catch(() => { if (!cancelled) setError('Could not load booking details.'); })
        .finally(() => { if (!cancelled) setLoading(false); });
    };

    fetchBooking();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <Skeleton />;

  // If API fails, fall back to a minimal static confirmation so the user
  // isn't left with a blank screen after a successful payment.
  if (error || !booking) {
    return <StaticConfirmation id={id} />;
  }

  const isTesla       = booking.guestKeyStatus !== 'not_applicable';
  const contractInfo  = CONTRACT_LABELS[booking.contractStatus];
  const guestKeyInfo  = GUEST_KEY_LABELS[booking.guestKeyStatus];
  const pointsEarned  = booking.loyaltyPointsEarned || 0;

  return (
    <div className="min-h-screen bg-gray-50 py-16 px-4">
      <div className="max-w-lg mx-auto space-y-6">

        {/* ── Hero ── */}
        <div className="text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Booking Confirmed!</h1>
          <p className="text-gray-500 text-sm mb-1">Your booking ID:</p>
          <p className="font-mono text-blue-600 font-semibold text-base">{id}</p>
        </div>

        {/* ── Loyalty points earned ── */}
        {pointsEarned > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
            <span className="text-2xl">⭐</span>
            <div>
              <p className="font-semibold text-blue-800">+{pointsEarned} Loyalty Points Earned</p>
              <p className="text-xs text-blue-600">Points are applied automatically to future bookings.</p>
            </div>
          </div>
        )}

        {/* ── Rental summary ── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Rental Summary</h2>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-gray-400 text-xs">Vehicle</dt>
              <dd className="font-medium text-gray-800">{vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : booking.vin}</dd>
            </div>
            <div>
              <dt className="text-gray-400 text-xs">Status</dt>
              <dd>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 capitalize">
                  {booking.status}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-gray-400 text-xs">Pickup</dt>
              <dd className="font-medium text-gray-800">{new Date(booking.startTime).toLocaleString()}</dd>
            </div>
            <div>
              <dt className="text-gray-400 text-xs">Return</dt>
              <dd className="font-medium text-gray-800">{new Date(booking.endTime).toLocaleString()}</dd>
            </div>
            <div>
              <dt className="text-gray-400 text-xs">Amount Paid</dt>
              <dd className="font-semibold text-gray-900">{formatCents(booking.totalAmountCents)}</dd>
            </div>
            <div>
              <dt className="text-gray-400 text-xs">Security Deposit Hold</dt>
              <dd className="font-medium text-gray-800">{formatCents(booking.depositAmountCents)}</dd>
            </div>
          </dl>
          <p className="mt-3 text-xs text-gray-400">
            The security deposit hold will be released within 24 hours of a clean return.
          </p>
        </div>

        {/* ── Rental agreement ── */}
        {contractInfo && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                📄 Rental Agreement
              </h2>
              <StatusBadge {...contractInfo} />
            </div>

            {booking.contractStatus === 'sent' || booking.contractStatus === 'viewed' ? (
              <div className="space-y-3">
                <p className="text-xs text-amber-600 font-medium">
                  ⚠ Vehicle access will not be activated until the agreement is signed.
                </p>
                {booking.contractSigningUrl ? (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <p className="mb-3 text-sm text-blue-900">
                      SignWell opens the secure agreement in a separate tab.
                    </p>
                    <a
                      href={booking.contractSigningUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                    >
                      Open & Sign Agreement ↗
                    </a>
                  </div>
                ) : (
                  <p className="text-sm text-gray-700">
                    Your rental agreement has been sent to your email. Please sign it to complete your booking.
                  </p>
                )}
              </div>
            ) : booking.contractStatus === 'renter_signed' ? (
              <p className="text-sm text-amber-700">
                Your signature is complete. Drive Dobias has been notified to countersign the agreement.
              </p>
            ) : booking.contractStatus === 'signed' ? (
              <p className="text-sm text-green-700">
                ✓ Signed on {new Date(booking.contractSignedAt).toLocaleDateString()}. You're all set!
              </p>
            ) : booking.contractStatus === 'expired' || booking.contractStatus === 'declined' ? (
              <p className="text-sm text-red-600">
                Your agreement has {booking.contractStatus}. Please contact us at{' '}
                <a href="mailto:hello@drivedobias.com" className="underline">hello@drivedobias.com</a>.
              </p>
            ) : booking.contractStatus === 'pending' ? (
              <p className="text-sm text-gray-600">
                Your rental agreement is being prepared and will be ready to sign shortly.
              </p>
            ) : null}
          </div>
        )}

        {/* ── Guest key / vehicle access (Tesla only) ── */}
        {guestKeyInfo && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                🔑 Vehicle Access
              </h2>
              <StatusBadge
                {...guestKeyInfo}
                href={guestKeyInfo.link ? (booking.guestAccessUrl || booking.guestKeyLink || undefined) : undefined}
              />
            </div>

            {booking.guestKeyStatus === 'active' && booking.guestKeyLink ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-700">Your vehicle access is active. Tap below to open the Tesla app:</p>
                <a
                  href={booking.guestKeyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-blue-700 transition"
                >
                  Open Vehicle Access →
                </a>
              </div>
            ) : booking.guestKeyStatus === 'pending' ? (
              <p className="text-sm text-gray-600">
                Vehicle access will be sent to your email <strong>30 minutes before pickup</strong>.
                Make sure the Tesla app is installed on your phone.
              </p>
            ) : booking.guestKeyStatus === 'failed' ? (
              <p className="text-sm text-red-600">
                There was an issue activating vehicle access. Our team has been alerted and will contact you shortly.
                You can also reach us at{' '}
                <a href="mailto:hello@drivedobias.com" className="underline">hello@drivedobias.com</a>.
              </p>
            ) : null}
          </div>
        )}

        {/* ── What's next panel ── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-800 mb-4">What happens next?</h2>

          {isTesla ? (
            /* Tesla vehicle steps */
            <ol className="space-y-4">
              {booking.contractStatus !== 'signed' && booking.contractStatus !== 'not_required' && (
                <Step n={1} color="amber">
                  Check your email for a <strong>rental agreement</strong> to sign. Vehicle access won't
                  be activated until it's signed.
                </Step>
              )}
              <Step n={booking.contractStatus === 'signed' ? 1 : 2} color="blue">
                <strong>30 minutes before pickup</strong>, you'll receive a vehicle access link by email.
              </Step>
              <Step n={booking.contractStatus === 'signed' ? 2 : 3} color="blue">
                Install the <strong>Tesla app</strong> on your phone and tap the link in your email to
                unlock and start the vehicle — no physical key needed.
              </Step>
              <Step n={booking.contractStatus === 'signed' ? 3 : 4} color="gray">
                After your rental, your <strong>security deposit hold</strong> will be released within
                24 hours of a clean return.
              </Step>
            </ol>
          ) : (
            /* Non-Tesla vehicle steps (Bolt, Sienna) */
            <ol className="space-y-4">
              <Step n={1} color="blue">
                <strong>30 minutes before pickup</strong>, you'll receive vehicle access instructions
                by email, including how to locate and access the vehicle.
              </Step>
              <Step n={2} color="blue">
                Follow the instructions in your email to access the vehicle at the pickup location.
              </Step>
              <Step n={3} color="gray">
                After your rental, your <strong>security deposit hold</strong> will be released within
                24 hours of a clean return.
              </Step>
            </ol>
          )}
        </div>

        {/* ── Tesla app install prompt ── */}
        {isTesla && (
          <div className="bg-gray-900 rounded-xl p-5 flex items-center gap-4">
            <span className="text-3xl">📱</span>
            <div className="flex-1">
              <p className="text-white font-semibold text-sm">Install the Tesla App</p>
              <p className="text-gray-400 text-xs mt-0.5">Required to access your Tesla vehicle.</p>
            </div>
            <div className="flex flex-col gap-2">
              <a
                href="https://apps.apple.com/us/app/tesla/id582007913"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs bg-white text-gray-900 px-3 py-1.5 rounded-lg font-medium hover:bg-gray-100 transition text-center"
              >
                App Store
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.teslamotors.tesla"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs bg-white text-gray-900 px-3 py-1.5 rounded-lg font-medium hover:bg-gray-100 transition text-center"
              >
                Google Play
              </a>
            </div>
          </div>
        )}

        {/* ── Actions ── */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Link
            to={`/booking/${id}`}
            className="flex-1 text-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
          >
            View Booking Details
          </Link>
          <Link
            to="/profile?tab=my-trips"
            className="flex-1 text-center border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition"
          >
            My Bookings
          </Link>
        </div>

        <p className="text-center text-xs text-gray-400">
          Questions? Email us at{' '}
          <a href="mailto:hello@drivedobias.com" className="text-blue-600 hover:underline">
            hello@drivedobias.com
          </a>
        </p>

      </div>
    </div>
  );
}

// ── Step helper component ─────────────────────────────────────────────────────

function Step({ n, color = 'blue', children }) {
  const colors = {
    blue:  'bg-blue-100 text-blue-700',
    amber: 'bg-amber-100 text-amber-700',
    gray:  'bg-gray-100 text-gray-500',
  };
  return (
    <li className="flex gap-3 text-sm text-gray-600">
      <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${colors[color]}`}>
        {n}
      </span>
      <span>{children}</span>
    </li>
  );
}

// ── Static fallback (shown if API call fails) ─────────────────────────────────

function StaticConfirmation({ id }) {
  return (
    <div className="min-h-screen bg-gray-50 py-16 px-4">
      <div className="max-w-lg mx-auto text-center">
        <div className="text-6xl mb-6">🎉</div>
        <h1 className="text-3xl font-bold text-gray-900 mb-3">Booking Confirmed!</h1>
        <p className="text-gray-500 mb-2">Your booking ID is:</p>
        <p className="font-mono text-blue-600 font-semibold text-lg mb-8">{id}</p>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-left space-y-4 mb-8">
          <h2 className="font-semibold text-gray-800">What happens next?</h2>
          <ol className="space-y-3 text-sm text-gray-600">
            <Step n={1} color="amber">
              You'll receive a <strong>rental agreement</strong> by email to sign (Tesla vehicles only).
            </Step>
            <Step n={2} color="blue">
              <strong>30 minutes before pickup</strong>, you'll receive vehicle access instructions by email.
            </Step>
            <Step n={3} color="blue">
              For Tesla vehicles, install the <strong>Tesla app</strong> and tap the link in your email to access the car.
            </Step>
            <Step n={4} color="gray">
              After your rental, your <strong>security deposit hold</strong> will be released within 24 hours of clean return.
            </Step>
          </ol>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to={`/booking/${id}`}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
          >
            View Booking Details
          </Link>
          <Link
            to="/profile?tab=my-trips"
            className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition"
          >
            My Bookings
          </Link>
        </div>

        <p className="mt-8 text-xs text-gray-400">
          Questions? Email us at{' '}
          <a href="mailto:hello@drivedobias.com" className="text-blue-600 hover:underline">
            hello@drivedobias.com
          </a>
        </p>
      </div>
    </div>
  );
}
